import { TeamSettingInstructionCoverageDialog } from "@/components/team/team-setting-instruction-coverage-dialog";
import { Badge } from "@/components/ui/badge";
import {
  CircularProgress,
  getCoverageScoreClassName,
} from "@/components/ui/circular-progress";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToastError } from "@/components/ui/use-toast";
import { useCoverageBatchListener } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO, TeamInstructionCoverageDTO } from "@shaarp/common";
import { CellContext } from "@tanstack/react-table";
import { useState } from "react";

function CoverageScoreCell({ team }: { team: AdminTeamDTO }) {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [score, setScore] = useState(team.instructionCoverageScore);

  useCoverageBatchListener(
    team.id,
    () => setIsEvaluating(true),
    (newScore) => {
      if (newScore !== null) {
        setScore(newScore);
      }
      setIsEvaluating(false);
    }
  );

  const handleRecalculate = async () => {
    try {
      setIsEvaluating(true);
      const { data } = await api.post<TeamInstructionCoverageDTO>(
        `/admin/team/${team.id}/instruction/coverage`
      );
      setScore(data.coverageScore);
    } catch {
      toastError();
    } finally {
      setIsEvaluating(false);
    }
  };

  return (
    <>
      <div
        className="flex justify-center cursor-pointer"
        onClick={() => setConfirmOpen(true)}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger>
            <CircularProgress
              value={score ?? 0}
              size={24}
              strokeWidth={3}
              showValue={false}
              indeterminate={isEvaluating}
              dashed={score == null}
              strokeClassName={
                score != null ? getCoverageScoreClassName(score) : undefined
              }
            />
          </TooltipTrigger>
          <TooltipContent>
            {score != null
              ? `${score}%`
              : t(
                  "admin.dataTableAdminTeams.coverageRecalculate.notCalculated"
                )}
          </TooltipContent>
        </Tooltip>
      </div>

      <TeamSettingInstructionCoverageDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        coverageScore={score}
        coverageReasoning={team.instructionCoverageReasoning}
        onForceRecalculate={handleRecalculate}
        isEvaluatingScore={isEvaluating}
      />
    </>
  );
}

/**
 * Stable component for the AI instruction cell.
 * Must be at module level so CoverageScoreCell inside it
 * is not unmounted/remounted on parent re-renders.
 */
export default function DataTableCellAdminInstruction({
  row,
}: CellContext<AdminTeamDTO, unknown>) {
  const team = row.original;
  const { t } = useTranslation();

  if (!team.instruction || !team.instructionLastUpdated) {
    return (
      <Badge variant={team.status === "enabled" ? "warning" : "outline"}>
        {t("admin.dataTableAdminTeams.badges.nonExistent")}
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline">
            {t("admin.dataTableAdminTeams.badges.ok")}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {t("admin.dataTableAdminTeams.tooltips.lastUpdated", {
            date: new Date(team.instructionLastUpdated).toLocaleString(),
          })}
        </TooltipContent>
      </Tooltip>
      <CoverageScoreCell team={team} />
    </div>
  );
}
